import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useNotifications } from 'utils/Notifications/NotificationContext';
import { BASE_URL } from 'utils/constant';

const NotificationPopup = () => {
  const { newNotification, dismissNewNotification, markAsRead } = useNotifications();
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [members, setMembers] = useState([]);

  const organizationId = JSON.parse(localStorage.getItem("organization"))?.id;

  const fetchMembers = async () => {
    if (!organizationId) return;
    const accessToken = localStorage.getItem("access");
    try {
      const response = await fetch(
        `${BASE_URL}/org/organization/${organizationId}/members/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken} `,
          },
        }
      );
      const data = await response.json();
      if (data.status) {
        setMembers(data.data);
      }
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  };

  useEffect(() => {
    if (newNotification) {
      setIsVisible(true);
      setIsAnimating(true);
      fetchMembers();

      // Auto dismiss after 5 seconds
      const timer = setTimeout(() => {
        handleDismiss();
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [newNotification]);

  const handleDismiss = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      dismissNewNotification();
    }, 300);
  };

  const handleClick = () => {
    if (!newNotification) return;
    if (markAsRead) markAsRead(newNotification.id);
    handleDismiss();
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""} `.toUpperCase() || "SM";
  };

  const getModuleTagStyles = (module) => {
    const styles = {
      Materials: "bg-blue-50 text-blue-600 border-blue-100",
      Tasks: "bg-purple-50 text-purple-600 border-purple-100",
      Snags: "bg-red-50 text-red-600 border-red-100",
      Finance: "bg-amber-50 text-amber-600 border-amber-100",
      Workers: "bg-orange-50 text-orange-600 border-orange-100",
    };
    return styles[module] || "bg-gray-50 text-gray-600 border-gray-100";
  };

  if (!isVisible || !newNotification) return null;

  const member = members.find(m => m.user.id === newNotification.actor_id);
  const userpic = member?.user?.userpic;
  const initials = member ? getInitials(member.user.first_name, member.user.last_name) : (newNotification.actor_name ? getInitials(newNotification.actor_name.split(' ')[0], newNotification.actor_name.split(' ')[1]) : "SM");
  const formattedMessage = newNotification.actor_name
    ? `${newNotification.actor_name} ${newNotification.message} `
    : newNotification.message;

  return (
    <div className="fixed bottom-4 right-4 z-[9999] w-full max-w-sm">
      <div
        className={`bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden transform transition-all duration-300 ${isAnimating
          ? 'translate-y-0 opacity-100 scale-100'
          : 'translate-y-10 opacity-0 scale-95'
          } `}
        style={{
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
        }}
      >
        <div
          onClick={handleClick}
          className="p-4 cursor-pointer hover:bg-gray-50 transition-colors flex gap-4"
        >
          {/* Avatar Area */}
          <div className="flex-shrink-0">
            {userpic ? (
              <img
                src={userpic}
                alt={newNotification.actor_name || "User"}
                className="w-12 h-12 rounded-full object-cover ring-2 ring-gray-50 shadow-sm"
              />
            ) : (
              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold shadow-sm ${newNotification.type === 'system' ? 'bg-[#58267F] text-white' : 'bg-[#7C3AED] text-white'} `}>
                {initials}
              </div>
            )}
          </div>

          {/* Text Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-sm font-bold text-gray-900 truncate">
                {newNotification.title || "New Notification"}
              </span>
              {newNotification.module && (
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border shadow-sm ${getModuleTagStyles(newNotification.module)} `}>
                  {newNotification.module}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 line-clamp-2 leading-tight">
              {formattedMessage}
            </p>
          </div>

          {/* Close Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDismiss();
            }}
            className="flex-shrink-0 p-1 rounded-full hover:bg-gray-200 transition-colors self-start"
          >
            <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
          </button>
        </div>

        {/* Progress bar for auto-dismiss */}
        <div className="h-1 bg-gray-100">
          <div
            className="h-full bg-[#3B0069] transition-all duration-[5000ms] ease-linear"
            style={{ width: isAnimating ? '100%' : '0%' }}
          />
        </div>
      </div>
    </div>
  );
};

export default NotificationPopup;
